import React, { useMemo, useState } from 'react';
import { format, parse } from 'date-fns';
import { de } from 'date-fns/locale';
import { Calendar as CalIcon } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';

/**
 * Date picker that mimics a native <input type=date> for the rest of the codebase.
 * Props:
 *  - value: string in ISO format (YYYY-MM-DD) — same contract as native input.
 *  - onChange(iso): receives the new value as ISO string.
 *  - placeholder, disabled, className, ...rest passed to the trigger button.
 *  - testId: forwarded to the trigger for stable test selectors.
 *  - blockedRanges: [{ from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }, …] — Tage in
 *    diesem Bereich sind im Kalender rot markiert und nicht auswählbar.
 *  - showLegend: bei true wird eine kleine Legende (Frei / Belegt) unter dem
 *    Kalender angezeigt.
 */
const DatePicker = ({
  value, onChange, placeholder = 'Datum wählen', disabled, className, testId, min, max,
  blockedRanges = [], showLegend = false,
}) => {
  const [open, setOpen] = useState(false);
  const parsed = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined;
  const display = parsed && !isNaN(parsed) ? format(parsed, 'dd.MM.yyyy', { locale: de }) : '';

  const minDate = min ? parse(min, 'yyyy-MM-dd', new Date()) : undefined;
  const maxDate = max ? parse(max, 'yyyy-MM-dd', new Date()) : undefined;

  // Normalisiere blockedRanges einmal in Timestamps für schnellen Vergleich.
  const rangesTs = useMemo(() => (blockedRanges || [])
    .filter((r) => r && r.from)
    .map((r) => {
      const from = new Date(r.from); from.setHours(0, 0, 0, 0);
      const to = new Date(r.to || r.from); to.setHours(23, 59, 59, 999);
      return { from: from.getTime(), to: to.getTime() };
    }), [blockedRanges]);

  const isBlocked = (d) => {
    const ts = d.getTime();
    return rangesTs.some((r) => ts >= r.from && ts <= r.to);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'inline-flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
            'placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !value && 'text-muted-foreground',
            className,
          )}
        >
          <span>{display || placeholder}</span>
          <CalIcon className="h-4 w-4 opacity-60 ml-2 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={parsed}
          // Kalender öffnet auf dem bereits gewählten Monat, statt auf dem
          // aktuellen Monat — verhindert Verwirrung, wenn das Datum weit
          // in der Zukunft liegt.
          defaultMonth={parsed || minDate || undefined}
          onSelect={(d) => {
            if (d) onChange?.(format(d, 'yyyy-MM-dd'));
            setOpen(false);
          }}
          disabled={(d) =>
            (minDate && d < minDate) || (maxDate && d > maxDate) || isBlocked(d) || false
          }
          modifiers={{ blocked: (d) => isBlocked(d) }}
          modifiersClassNames={{
            blocked: 'bg-red-100 text-red-500 line-through font-semibold',
          }}
          locale={de}
          weekStartsOn={1}
          initialFocus
        />
        {showLegend && (
          <div className="border-t border-neutral-100 p-2 flex items-center gap-4 text-[11px] text-neutral-600 bg-neutral-50">
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-white border border-neutral-300" /> Frei</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-red-100 border border-red-300" /> Belegt</span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
